import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowLeft } from "lucide-react";
import { Highlight } from "@/lib/highlight";
import { sanitize } from "@/lib/sanitize";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;
const ALL_EVENTS = "__all__";

export default function SportPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sport, setSport] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordTypes, setRecordTypes] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [activeCat, setActiveCat] = useState("all");
  const [activeType, setActiveType] = useState(null); // null = not yet initialized
  const [activeEvent, setActiveEvent] = useState(searchParams.get("event") || ALL_EVENTS);

  useEffect(() => {
    api.get(`/sports/${slug}`).then(r => setSport(r.data));
  }, [slug]);

  useEffect(() => {
    if (!sport) return;
    // Small lookups only — the actual records are paged from the server below.
    Promise.all([
      api.get(`/record-types?sport_id=${sport.id}`),
      api.get(`/categories?sport_id=${sport.id}`),
    ]).then(([rt, cc]) => {
      setRecordTypes(rt.data);
      setCategoryOrder(cc.data);
    });
  }, [sport]);

  // Debounce the search input to avoid a request per keystroke.
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Server-paginated record fetch — reloads on any tab / filter / search change.
  const [total, setTotal] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);
  useEffect(() => {
    if (!sport || activeType === null) return;
    let cancelled = false;
    setLoadingRecords(true);
    const params = {
      sport_id: sport.id,
      status: "approved",
      record_type: activeType,
      page: pageIdx,
      page_size: PAGE_SIZE,
    };
    if (activeCat !== "all") params.category = activeCat;
    if (debouncedQ) params.search = debouncedQ;
    if (groupByEvents && activeEvent !== ALL_EVENTS) {
      params.event_column = eventColumn;
      params.event_value = activeEvent;
    }
    api.get("/records", { params }).then(res => {
      if (cancelled) return;
      setRecords(res.data.items || []);
      setTotal(res.data.total || 0);
    }).catch(() => { if (!cancelled) { setRecords([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoadingRecords(false); });
    return () => { cancelled = true; };
  }, [sport, activeType, activeCat, activeEvent, debouncedQ, pageIdx]); // eslint-disable-line

  // Fetch the total approved count once (for the header stat) so the "N approved records"
  // remains accurate independent of the current filter/paging state.
  useEffect(() => {
    if (!sport) return;
    api.get("/records", { params: { sport_id: sport.id, status: "approved", page: 0, page_size: 1 } })
      .then(res => setTotalApproved(res.data.total || 0));
  }, [sport]);

  // Compute the record type tabs from the /record-types list. Prefer types that
  // have at least one approved record so the default tab isn't visually empty.
  const typeTabs = useMemo(() => {
    const defined = recordTypes.map(t => ({
      id: t.id, name: t.name, description: t.description,
      hasRecords: (t.record_count || 0) > 0,
    }));
    if (defined.length === 0) {
      return [{ id: "__general__", name: "General", description: "", isGeneral: true, hasRecords: true }];
    }
    return defined;
  }, [recordTypes]);

  // Initialize / re-sync the active tab whenever the tab set changes.
  useEffect(() => {
    if (typeTabs.length === 0) return;
    const validValues = new Set(typeTabs.map(t => t.isGeneral ? "" : t.name));
    // Honor ?rt= URL param first (deep-linking from Master Search)
    const rtParam = searchParams.get("rt");
    if (activeType === null && rtParam && validValues.has(rtParam)) {
      setActiveType(rtParam); return;
    }
    if (activeType === null || !validValues.has(activeType)) {
      // Prefer first tab that actually has approved records
      const first = typeTabs.find(t => t.hasRecords) || typeTabs[0];
      setActiveType(first.isGeneral ? "" : first.name);
    }
  }, [typeTabs, activeType]); // eslint-disable-line

  // The active record type's config drives event grouping.
  const activeRecordType = useMemo(
    () => recordTypes.find(t => t.name === activeType) || null,
    [recordTypes, activeType]
  );
  const eventColumn = activeRecordType?.group_by_event_tabs ? (activeRecordType.event_column || null) : null;
  const groupByEvents = !!(eventColumn && (activeRecordType?.columns || []).includes(eventColumn));

  // Facet lookup: distinct categories & event values for the active record type,
  // computed on the server via db.distinct() (fast even at 40k+ records).
  const [facets, setFacets] = useState({ categories: [], events: [] });
  useEffect(() => {
    if (!sport || activeType === null) return;
    let cancelled = false;
    const params = { sport_id: sport.id, record_type: activeType };
    if (eventColumn) params.event_column = eventColumn;
    api.get("/records/facets", { params })
      .then(res => { if (!cancelled) setFacets(res.data || { categories: [], events: [] }); })
      .catch(() => { if (!cancelled) setFacets({ categories: [], events: [] }); });
    return () => { cancelled = true; };
  }, [sport, activeType, eventColumn]);

  // Sub-tabs come from the facets endpoint.
  const eventTabs = useMemo(() => (groupByEvents ? (facets.events || []) : []), [groupByEvents, facets]);

  // When the record type changes / grouping changes, reset the sub-tab selection sensibly.
  useEffect(() => {
    if (!groupByEvents) { setActiveEvent(ALL_EVENTS); return; }
    const urlEvent = searchParams.get("event");
    if (urlEvent && (urlEvent === ALL_EVENTS || eventTabs.includes(urlEvent))) {
      setActiveEvent(urlEvent);
    } else if (!eventTabs.includes(activeEvent) && activeEvent !== ALL_EVENTS) {
      setActiveEvent(ALL_EVENTS);
    }
  }, [groupByEvents, eventTabs.join("|")]); // eslint-disable-line

  // Records already come from the server pre-filtered & paginated for the active
  // (record_type, category, event, search) tuple — we only group by category here for display.
  const grouped = useMemo(() => {
    const g = {};
    for (const r of records) { (g[r.category || "Uncategorized"] ||= []).push(r); }
    return g;
  }, [records]);

  // Auto-select the default category (if defined) whenever the active tab changes.
  useEffect(() => {
    if (!categoryOrder || categoryOrder.length === 0) return;
    if (activeType === null) return;
    const validForTab = new Set(facets.categories || []);
    const def = categoryOrder.find(c => c.is_default && validForTab.has(c.name));
    if (def && (activeCat === "all" || !validForTab.has(activeCat))) {
      setActiveCat(def.name);
    }
  }, [activeType, categoryOrder, facets]); // eslint-disable-line

  const categoriesForActiveType = useMemo(() => {
    const set = new Set(facets.categories || []);
    const orderMap = new Map(categoryOrder.map((c, i) => [c.name, i]));
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const ai = orderMap.has(a) ? orderMap.get(a) : 9999;
      const bi = orderMap.has(b) ? orderMap.get(b) : 9999;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });
    return arr;
  }, [facets, categoryOrder]);

  const activeTypeMeta = typeTabs.find(t => (t.isGeneral ? "" : t.name) === activeType);
  // Dynamic columns for the active record type — no legacy fallback.
  const dynamicCols = (activeRecordType?.columns || []).filter(Boolean);

  // Reset page 0 whenever any filter changes.
  useEffect(() => { setPageIdx(0); }, [activeType, activeCat, activeEvent, debouncedQ]);

  // Keep URL params in sync with the current view so links (from Master Search
  // or from copy/paste) faithfully re-open the exact sub-tab.
  useEffect(() => {
    if (activeType === null) return;
    const next = new URLSearchParams(searchParams);
    if (activeType) next.set("rt", activeType); else next.delete("rt");
    if (groupByEvents && activeEvent && activeEvent !== ALL_EVENTS) next.set("event", activeEvent); else next.delete("event");
    if (q) next.set("q", q); else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [activeType, activeEvent, groupByEvents, q]); // eslint-disable-line

  if (!sport) return <div className="p-8">Loading...</div>;

  return (
    <main className="max-w-7xl mx-auto px-6 py-8" data-testid="sport-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A1128] mb-4" data-testid="back-to-sports">
        <ArrowLeft className="w-4 h-4"/> All sports
      </Link>

      <div className="relative h-52 md:h-64 border-2 mb-4 overflow-hidden"
           style={{ borderColor: sport.primary_color || "var(--brand-primary)" }}>
        {sport.image_url && <img src={sport.image_url} alt={sport.name} className="absolute inset-0 w-full h-full object-cover"/>}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30"/>
        <div className="relative h-full flex flex-col justify-end p-6 text-white">
          {sport.season && <p className="font-overline text-xs" style={{ color: sport.accent_color || "var(--brand-accent)" }}>{sport.season}</p>}
          {!sport.season && <p className="font-overline text-xs" style={{ color: sport.accent_color || "var(--brand-accent)" }}>Record Book</p>}
          <div className="flex items-end gap-4">
            {sport.logo_url && <img src={sport.logo_url} alt="" className="h-14 md:h-16 object-contain bg-white/95 p-1"/>}
            <h1 className="font-heading text-5xl md:text-6xl tracking-tight" data-testid="sport-title">{sport.name.toUpperCase()}</h1>
          </div>
          {sport.tagline && <p className="text-white/85 text-sm mt-1">{sport.tagline}</p>}
          <p className="text-sm text-white/70 mt-1">{totalApproved} approved records · {typeTabs.length} {typeTabs.length === 1 ? "record type" : "record types"}</p>
        </div>
      </div>

      {(sport.head_coach || sport.home_venue || sport.conference || sport.mascot || sport.twitter_url || sport.instagram_url) && (
        <div className="mb-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-600 border-l-4 pl-3 py-1" style={{ borderColor: sport.accent_color || "var(--brand-accent)" }} data-testid="sport-meta">
          {sport.head_coach && <span><strong className="text-slate-800">Head Coach:</strong> {sport.head_coach}</span>}
          {sport.home_venue && <span><strong className="text-slate-800">Home Venue:</strong> {sport.home_venue}</span>}
          {sport.conference && <span><strong className="text-slate-800">Conference:</strong> {sport.conference}</span>}
          {sport.mascot && <span><strong className="text-slate-800">Mascot:</strong> {sport.mascot}</span>}
          {sport.twitter_url && <a href={sport.twitter_url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: sport.primary_color || "var(--brand-primary)" }}>Twitter →</a>}
          {sport.instagram_url && <a href={sport.instagram_url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: sport.primary_color || "var(--brand-primary)" }}>Instagram →</a>}
        </div>
      )}

      {typeTabs.length > 1 && (
        <div className="mb-4 border-b-2" style={{ borderColor: sport.primary_color || "var(--brand-primary)" }} data-testid="record-type-tabs">
          <div className="flex flex-wrap gap-1">
            {typeTabs.map(t => {
              const val = t.isGeneral ? "" : t.name;
              const active = activeType === val;
              return (
                <button key={t.id} onClick={() => { setActiveType(val); setActiveCat("all"); }}
                  className={`px-4 py-2.5 text-sm font-heading tracking-wide border-2 border-b-0 -mb-[2px] transition ${active ? "text-white" : "bg-white hover:bg-slate-50"}`}
                  style={{
                    borderColor: sport.primary_color || "var(--brand-primary)",
                    background: active ? (sport.primary_color || "var(--brand-primary)") : "white",
                    color: active ? "white" : (sport.primary_color || "var(--brand-primary)"),
                  }}
                  data-testid={`record-type-tab-${t.isGeneral ? "general" : t.name}`}>
                  {t.name.toUpperCase()}
                  {!t.hasRecords && <span className="ml-1 opacity-50 text-[10px]">(0)</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeTypeMeta?.description && (
        <div
          className="prose prose-sm max-w-2xl mb-4 border-l-4 pl-4 py-2 bg-slate-50/60"
          style={{ borderColor: sport.accent_color || "var(--brand-accent)" }}
          data-testid="active-type-description"
          dangerouslySetInnerHTML={{ __html: sanitize(activeTypeMeta.description) }}
        />
      )}

      {groupByEvents && eventTabs.length > 0 && (
        <div className="mb-5" data-testid="event-sub-tabs">
          <p className="font-overline text-[10px] text-slate-500 mb-1.5">Events</p>
          <div
            className="overflow-x-auto scrollbar-thin"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="inline-flex gap-1 pb-1">
              {[ALL_EVENTS, ...eventTabs].map(ev => {
                const active = activeEvent === ev;
                const label = ev === ALL_EVENTS ? "All Events" : ev;
                return (
                  <button
                    key={ev}
                    onClick={() => setActiveEvent(ev)}
                    className="px-3 py-1.5 text-xs font-heading tracking-wide border-2 whitespace-nowrap transition-colors"
                    style={{
                      borderColor: sport.primary_color || "var(--brand-primary)",
                      background: active ? (sport.primary_color || "var(--brand-primary)") : "white",
                      color: active ? (sport.accent_color || "white") : (sport.primary_color || "var(--brand-primary)"),
                    }}
                    data-testid={`event-sub-tab-${ev === ALL_EVENTS ? "all" : ev}`}
                  >
                    {label.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 space-y-3">
        <div className="relative w-full max-w-2xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"/>
          <Input placeholder="Search by athlete, school, or year..." value={q} onChange={e => setQ(e.target.value)}
            className="pl-9 h-11 rounded-sm border-2 border-slate-300 focus-visible:border-[#0A1128] focus-visible:ring-0 bg-white text-base" data-testid="search-input"/>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          <button onClick={() => setActiveCat("all")}
            className={`px-3 py-1.5 text-xs font-heading tracking-wide border-2 border-[#0A1128] ${activeCat === "all" ? "bg-[#0A1128] text-white" : "bg-white text-[#0A1128] hover:bg-slate-100"}`}
            data-testid="cat-filter-all">ALL CATEGORIES</button>
          {categoriesForActiveType.map(c => (
            <button key={c} onClick={() => setActiveCat(c)}
              className={`px-3 py-1.5 text-xs font-heading tracking-wide border-2 border-[#0A1128] ${activeCat === c ? "bg-[#0A1128] text-white" : "bg-white text-[#0A1128] hover:bg-slate-100"}`}
              data-testid={`cat-filter-${c}`}>{c.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {loadingRecords && total === 0 && (
        <div className="p-8 text-center text-slate-500 font-overline tracking-wider text-xs">Loading records…</div>
      )}

      {!loadingRecords && total === 0 && (
        <div className="border-2 border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="no-records">
          No records found{activeTypeMeta ? ` in ${activeTypeMeta.name}` : ""}. {debouncedQ && "Try a different search."}
        </div>
      )}

      {total > 0 && dynamicCols.length === 0 && (
        <div className="border-2 border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm">
          No fields defined for <strong>{activeType || "this record type"}</strong>. An admin must import a CSV to define columns.
        </div>
      )}

      {total > 0 && dynamicCols.length > 0 && (
        <>
          {Object.entries(grouped).map(([cat, rows]) => (
            <section key={cat} className="mb-8" data-testid={`category-section-${cat}`}>
              <div className="flex items-baseline gap-3 mb-2 border-b-2 pb-2" style={{ borderColor: sport.primary_color || "var(--brand-primary)" }}>
                <h2 className="font-heading text-2xl tracking-tight" style={{ color: sport.primary_color || "var(--brand-primary)" }}>{cat}</h2>
                <span className="font-overline text-[10px] text-slate-500">{rows.length} on this page</span>
              </div>
              <div className="border border-slate-300 bg-white overflow-x-auto">
                <Table>
                  <TableHeader style={{ background: sport.primary_color || "var(--brand-primary)" }}>
                    <TableRow className="hover:brightness-100" style={{ background: sport.primary_color || "var(--brand-primary)" }}>
                      {dynamicCols.map(col => (
                        <TableHead key={col} className="text-white font-heading tracking-wide whitespace-nowrap">{col}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`record-row-${r.id}`}>
                        {dynamicCols.map(col => (
                          <TableCell key={col} className="py-2 text-sm whitespace-nowrap">
                            <Highlight text={r.dynamic_columns?.[col] ?? ""} q={q}/>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          ))}
          <div className="border border-slate-300 bg-white">
            <Pagination
              page={pageIdx}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPageIdx}
              testidPrefix="sport-pagination"
            />
          </div>
        </>
      )}
    </main>
  );
}
